import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
/**
 * Learner Readiness Score schemas.
 *
 * See `docs/fatigue-score/README.md` for the full design spec and
 * `docs/fatigue-score/algorithm.md` for the implementation reference.
 *
 * Score directionality: 1.0 = peak readiness (fresh), 0.0 = exhausted (take a break).
 * The 5-bar widget mirrors this — full bars = ready.
 */
// ============================================================================
// Trigger enum (matches CHECK constraint on fatigue_scores.trigger).
// ============================================================================
export const ReadinessTriggerSchema = z.enum(['step_submit', 'episode_close', 'session_resume']);
// ============================================================================
// JSONB feature vector schemas.
//
// Two distinct shapes ride in `fatigue_scores.features`:
//   - StepSubmitFeaturesSchema: full feature vector from a step-submit compute
//   - SessionResumeFeaturesSchema: recovery-curve metadata (no algorithm run)
//
// The single combined schema below is a discriminated union via a flag field
// so callers can parse without knowing which kind they got.
// ============================================================================
export const StepSubmitFeaturesSchema = z
    .object({
    rt_cv: z.number(),
    rt_z: z.number(),
    accuracy_ewma: z.number(),
    lapse_rate: z.number(),
    time_in_episode_min: z.number(),
    confidence_calibration_drift: z.number().nullable(),
    behavioural_uncertainty: z.number(),
    behavioural_uncertainty_gated: z.number(),
    window_size: z.number().int(),
    window_decay_tau_min: z.number(),
    /**
     * Effective sample size (Kish) of the time-decay weights (algo v1.10.0).
     * ESS = (Σwᵢ)² / Σ(wᵢ²). Collapses toward 1 when one weight dominates,
     * approaches `window_size` when weights are equal. Used in place of raw
     * window_size for the displayed-score trust blend — gives a smooth
     * gradient through the recovery window rather than a cliff at the
     * 30-min episode boundary.
     */
    effective_sample_size: z.number().min(0),
    /**
     * Magnitude of declining-accuracy trend within the window (algo v1.11.0).
     * `max(0, older_half_acc - recent_half_acc)` using unweighted half-means.
     * Zero when flat/improving; positive when recent half is worse. Primary
     * fatigue signal for revise sessions where absolute accuracy isn't
     * meaningful (forgetting expected).
     *
     * Optional + defaults to 0 for backward compatibility: pre-v1.11.0 rows
     * in `fatigue_scores` don't have this field, and the schema is `.strict()`.
     * Treating a missing field as "no trend signal" preserves backward
     * parsing for telemetry / analytics reads of historical rows.
     */
    performance_trend_decline: z.number().min(0).max(1).optional().default(0),
    session_type: z.enum(['learn', 'revise']),
    step_type: z.string(),
    /** Bloom's level (1-6) of the submitted step, null if unclassified. */
    blooms_level: z.number().int().min(1).max(6).nullable(),
    /** Within-Bloom's difficulty (1-5), null if unclassified. */
    difficulty: z.number().int().min(1).max(5).nullable(),
    used_population_baseline: z.boolean(),
    /**
     * Raw score from the logit aggregation, before the small-window confidence
     * blend (algorithm v1.7.0). Persisted alongside the displayed score so
     * offline analysis can compare what the algorithm "thought" against what
     * the user saw.
     */
    raw_score: z.number().min(0).max(1),
    /**
     * Confidence multiplier applied to the raw score (algorithm v1.7.0). Ranges
     * from 0 (window too small to trust) to 1 (window large enough for full
     * confidence). The displayed score is `raw × confidence + fresh_default ×
     * (1 - confidence)`.
     */
    confidence: z.number().min(0).max(1),
    recovery_applied: z.literal(false).optional(),
})
    .strict();
export const SessionResumeFeaturesSchema = z
    .object({
    recovery_applied: z.literal(true),
    recovery_elapsed_min: z.number(),
    source_score: z.number().min(0).max(1),
    tau_recovery: z.number(),
    algorithm_version: z.string(),
})
    .strict();
/**
 * Combined JSONB schema covering both step-submit and session-resume rows.
 * Use `ReadinessFeaturesJsonbSchema.parse(row.features)` at read-time to recover
 * a typed object without resorting to `any` (per CLAUDE.md no-any rule).
 */
export const ReadinessFeaturesJsonbSchema = z.union([
    StepSubmitFeaturesSchema,
    SessionResumeFeaturesSchema,
]);
// ============================================================================
// Score snapshot — what the readiness service produces and persists.
// ============================================================================
export const ReadinessScoreSnapshotSchema = z
    .object({
    score: z.number().min(0).max(1),
    bars: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    isColdStart: z.boolean(),
    algorithmVersion: z.string(),
    trigger: ReadinessTriggerSchema,
    features: ReadinessFeaturesJsonbSchema,
})
    .strict();
// ============================================================================
// API response shape for GET /me/readiness (the out-of-session read endpoint).
// ============================================================================
export const ReadinessReadResponseSchema = z
    .object({
    score: z.number().min(0).max(1),
    bars: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    isColdStart: z.boolean(),
    isStale: z.boolean(),
    elapsedMinutesSinceCompute: z.number().nullable(),
    algorithmVersion: z.string(),
    /**
     * Active learning minutes within the user's current open episode. Null when
     * no episode is active (no recent learning, or last episode was just closed
     * by the staleness check). Computed at request time so this reflects the
     * present state, not a frozen value from the last step submit (algo v1.8.0).
     */
    activeMinutesInEpisode: z.number().nullable(),
    /**
     * Start of the user's current open episode (ms since epoch), or null if no
     * episode is active. Same freshness guarantee as `activeMinutesInEpisode`.
     */
    episodeStartedAtMs: z.number().nullable(),
})
    .strict()
    .openapi('ReadinessReadResponse');
// ============================================================================
// Fields added to existing response payloads (step-submit, session-load).
// Shared as a sub-schema so api/web/core all use the same field types.
// ============================================================================
export const ReadinessPayloadFieldsSchema = z.object({
    readinessScore: z.number().min(0).max(1),
    readinessBars: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    isColdStart: z.boolean(),
    algorithmVersion: z.string(),
    readinessModalDismissedAt: z.string().nullable(),
    readinessModalCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=readiness.schema.js.map